/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import { useState, useCallback, useRef } from 'react';

interface StreamEvent {
  type: 'metrics' | 'content' | 'visualization' | 'done' | 'error';
  data?: any;
  text?: string;
  message?: string;
}

interface UseStreamingChatOptions {
  onMetrics?: (metrics: any) => void;
  onVisualization?: (data: any) => void;
  onError?: (error: string) => void;
}

export const useStreamingChat = (options: UseStreamingChatOptions = {}) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [responseData, setResponseData] = useState<any>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendStreamingMessage = useCallback(async (
    message: string,
    brandId: string,
    context: any[] = []
  ) => {
    setIsStreaming(true);
    setStreamingContent('');
    setResponseData(null);

    try {
      // Use real endpoint that connects to actual survey data
      const response = await fetch('/api/chat/real', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          brand_id: brandId,
          context
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Simple JSON response instead of streaming
      const data = await response.json();
      
      // If there's an error detail, show it to the user
      if (data.detail) {
        setStreamingContent(`**Error**: ${data.detail}\n\nThe backend is currently experiencing issues. This usually indicates:\n- Vector database connection problems\n- MongoDB connection issues\n- Import/configuration errors\n\nThe enhanced chat features with charts and RAG integration are being debugged.`);
        setIsStreaming(false);
        return;
      }
      
      // Handle metrics if present
      if (data.metrics) {
        options.onMetrics?.(data.metrics);
      }
      
      // Set the complete response
      if (data.response) {
        setStreamingContent(data.response);
      }
      
      // Store full response data for enhanced components
      setResponseData(data);
      
      // Mark as done
      setIsStreaming(false);
    } catch (error) {
      console.error('Streaming error:', error);
      options.onError?.(error instanceof Error ? error.message : 'Streaming failed');
      setIsStreaming(false);
    }
  }, [options]);

  const stopStreaming = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  return {
    isStreaming,
    streamingContent,
    responseData,
    sendStreamingMessage,
    stopStreaming
  };
};